import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { QrCode, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={next} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const ok = await login(email.trim(), password);
    setLoading(false);
    if (ok) {
      toast.success("Welcome back!");
      navigate(next);
    } else {
      setErr("Invalid email or password.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="max-w-md mx-auto w-full px-4 sm:px-6 flex-1 flex flex-col justify-center py-12">
        <Link to="/" className="flex items-center gap-2.5 justify-center mb-8" data-testid="login-logo">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow">
            <QrCode className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-black text-2xl tracking-tight text-slate-900">Qdurator</span>
        </Link>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="login-heading">
            Log in to your dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage your dynamic QR campaigns.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-5">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
                Email
              </Label>
              <Input
                data-testid="login-email-input"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="h-11 rounded-xl"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                  data-testid="login-forgot-password-link"
                >
                  Forgot?
                </Link>
              </div>
              <Input
                data-testid="login-password-input"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11 rounded-xl"
              />
            </div>

            {err && (
              <div
                data-testid="login-error"
                className="text-sm bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg"
              >
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Log in <ArrowRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-500">
            No account yet?{" "}
            <Link
              to="/register"
              className="text-blue-600 hover:text-blue-700 font-semibold"
              data-testid="login-goto-register"
            >
              Create one
            </Link>
          </div>
        </div>

        <div className="text-center mt-8">
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
